import { createFileRoute } from '@tanstack/react-router'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import { useState } from 'react'
import { Settings, Sliders, Shield, Mail, DollarSign, Zap } from 'lucide-react'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { useConfig, useConfigCategories, useUpdateConfig } from '@/features/super-admin/hooks/use-config'
import { ConfigForm } from '@/features/super-admin/components/config/ConfigForm'

export const Route = createFileRoute('/super-admin/settings/')({
  component: SettingsPage,
})

function SettingsPage() {
  const { onMenuToggle } = useMenuToggle()
  const [selectedCategory, setSelectedCategory] = useState<string>('')

  const { data: categories, isLoading: categoriesLoading } = useConfigCategories()
  const { data: configs, isLoading: configsLoading } = useConfig(selectedCategory || undefined)
  const updateMutation = useUpdateConfig()

  const handleUpdate = async (key: string, value: unknown) => {
    await updateMutation.mutateAsync({ key, value })
  }

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, React.ReactNode> = {
      general: <Settings className="w-5 h-5" />,
      billing: <DollarSign className="w-5 h-5" />,
      email: <Mail className="w-5 h-5" />,
      security: <Shield className="w-5 h-5" />,
      features: <Zap className="w-5 h-5" />,
      limits: <Sliders className="w-5 h-5" />,
    }
    return icons[category] ?? <Settings className="w-5 h-5" />
  }

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      general: 'Geral',
      billing: 'Cobrança',
      email: 'Email',
      security: 'Segurança',
      features: 'Funcionalidades',
      limits: 'Limites',
    }
    return labels[category] ?? category
  }

  return (
    <div className="flex flex-col h-full">
      <SuperAdminHeader
        title="Configurações"
        subtitle="Gerencie as configurações globais do sistema"
        onMenuToggle={onMenuToggle}
      />
      <div className="flex-1 overflow-auto p-4 md:p-6 max-w-7xl mx-auto w-full">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Category Sidebar */}
        <div className="w-full md:w-64 md:flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden md:sticky md:top-6">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-700">Categorias</h3>
            </div>
            <div className="p-2">
              <button
                onClick={() => setSelectedCategory('')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedCategory === ''
                    ? 'bg-teal-50 text-teal-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Settings className="w-5 h-5" />
                Todas
              </button>
              {categoriesLoading ? (
                <div className="p-4">
                  <div className="animate-pulse space-y-2">
                    <div className="h-8 bg-gray-100 rounded" />
                    <div className="h-8 bg-gray-100 rounded" />
                    <div className="h-8 bg-gray-100 rounded" />
                  </div>
                </div>
              ) : (
                categories?.map((category: string) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedCategory === category
                        ? 'bg-teal-50 text-teal-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {getCategoryIcon(category)}
                    {getCategoryLabel(category)}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Config Form */}
        <div className="flex-1">
          <ConfigForm
            configs={configs || []}
            loading={configsLoading}
            onUpdate={handleUpdate}
            updating={updateMutation.isPending}
          />
        </div>
      </div>
      </div>
    </div>
  )
}
